import Box from "@mui/material/Box";
import ExportButton from "components/common/Export";
import { exportURLs } from "components/common/Export/ExportAPIEndpoints";
import { fetchmembersInfo } from "components/common/Filters/Filters.slice";
import { filterPage } from "components/common/Filters/FiltersHelper";
import GlobalFilterDescription from "components/common/Filters/GlobalFilterDescription";
import GlobalFilters from "components/common/Filters/GlobalFilters";
import KeyMetrics from "components/common/KeyMetrics/KeyMetrics";
import BasicTable from "components/common/Table";
import Toggle from "components/common/Toggle";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { messages } from "utils/CommonConstants";
import { formFilterRequestPayload, getMoreValues, getTableColor, getUserTypeTag } from "utils/CommonFunctions";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { serviceTypes } from "utils/Types";
import { fetchMembers, fetchMembersSnapshot } from "../IndividualProperty.slice";
import { headers } from "../TableHeadersConfig";
import "./index.scss";

function Members() {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const translate = useTranslate();

  const {
    members: { list, snapshot },
    showIndividualPropertyLoader
  } = useAppSelector((state) => state.individualProperty);

  const inncodeState = useAppSelector((state) => state.app.innCode);
  const inncode = params.inncode || inncodeState;

  const [toggle, setToggle] = useState(false);

  const globalFiltersforPage = useAppSelector(
    (state) => state.globalFilters[filterPage.individualPropertyMembersDisplay]
  );
  const filterRequestPayload = globalFiltersforPage?.modifiedFilter || globalFiltersforPage?.defaultFilter || [];
  const formattedFilterRequestPayload = formFilterRequestPayload(filterRequestPayload);
  const searchText = globalFiltersforPage.searchText;

  useEffect(() => {
    const payload = {
      inncode: [inncode],
      searchText: searchText,
      excludeAbovePropertyUsers: !toggle,
      ...formattedFilterRequestPayload
    };

    dispatch(fetchMembers(payload));
  }, [searchText, toggle, inncode, filterRequestPayload]);

  useEffect(() => {
    const payload = {
      inncode: [inncode],
      excludeAbovePropertyUsers: true,
      ...formattedFilterRequestPayload
    };

    dispatch(fetchMembersSnapshot(payload));
  }, [inncode, filterRequestPayload]);

  const getMembersList = (searchValue: string) => {
    const props: any = {
      searchText: searchValue,
      innCode: inncode,
      page: 0,
      size: 100
    };
    dispatch(fetchmembersInfo(props));
  };

  return (
    !showIndividualPropertyLoader && (
      <Box className="members-container">
        <Box className="filters-panel">
          <Box>
            <GlobalFilters
              isSearchBarVisible
              page={filterPage.individualPropertyMembersDisplay}
              getMembersList={getMembersList}
              isPreDefinedNav={true}
              searchedText={searchText}
            />
          </Box>
        </Box>
        <GlobalFilterDescription
          page={filterPage.individualPropertyMembersDisplay}
          bgColor={tokens.Background.General.Primary}
        />
        <Box mt={"16px"}>
          <KeyMetrics
            helperText="*Last 30 Days"
            keyMetricsData={[
              {
                isPrimary: true,
                subTitle: "Total Members",
                data: snapshot.totalMembers
              },
              { subTitle: "*Active", data: snapshot.active, showBorder: true },
              {
                subTitle: "*Inactive",
                data: snapshot.inactive,
                showBorder: true
              },
              {
                subTitle: "*Overdue",
                data: snapshot.overdue,
                showBorder: false
              }
            ]}
          />
        </Box>
        <Box className="table-container">
          <Box className="table-actions">
            <Box className="table-actions-right">
              <Toggle
                labelOn={translate("common.includeAboveProperty")}
                labelOff={translate("common.includeAboveProperty")}
                isToggleEnabled={toggle}
                onChange={(value: boolean) => setToggle(value)}
                isLabelLeftSide={true}
                sx={{ marginLeft: "8px" }}
              />
              <ExportButton
                URL={exportURLs.individualProperty_members}
                data={{
                  inncode: [inncode],
                  excludeAbovePropertyUsers: !toggle,
                  ...formattedFilterRequestPayload
                }}
                serviceType={serviceTypes.userplan}
              />
            </Box>
          </Box>

          <BasicTable
            isTablePaginated={false}
            totalRecords={list?.length}
            noDataMessage={messages.noAbovePropertiesMembers}
            data={{
              headers,
              rows: list?.map((member: any) => {
                return {
                  data: [
                    {
                      content: (
                        <Typography
                          variant={"body2"}
                          showBold
                          sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/userview/${member?.planAccessTo}?inncode=${inncode}`)}
                        >
                          {getUserNameWithConditionalCheck(member?.displayName, member?.firstName, member?.lastName)}
                        </Typography>
                      ),
                      showBold: true
                    },
                    {
                      content: getMoreValues(member?.roles),
                      tooltip: {
                        title: "Role",
                        list: member?.roles?.map((role: any) => {
                          return { name: role };
                        })
                      }
                    },
                    { content: member?.roleCategory },
                    {
                      content: getUserTypeTag(member?.lobbyUserType)
                    },
                    {
                      content: member.totalPlansCount,
                      tooltip: {
                        title: "Plans",
                        list: [
                          { name: "New", value: member.newPlansCount },
                          { name: "In Progress", value: member.inProgressPlansCount },
                          { name: "Completed w/i 90 days", value: member.completedPlansCount },
                          { name: "Overdue", value: member.overduePlansCount }
                        ]
                      }
                    },
                    {
                      content: member.totalCardsCount,
                      tooltip: {
                        title: "Assigned Actions",
                        list: [
                          { name: "New", value: member.newAssignedCardsCount },
                          { name: "In Progress", value: member.inProgressAssignedCardsCount },
                          { name: "Completed w/i 90 days", value: member.completedAssignedCardsCount },
                          { name: "Overdue", value: member.overdueCardsCount }
                        ]
                      }
                    },
                    {
                      content: member.engagement,
                      color: getTableColor(member.engagement)
                    }
                  ]
                };
              })
            }}
          />
        </Box>
      </Box>
    )
  );
}

export default Members;
